import React, { useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Row, Col, Tabs, Tab, Badge, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faPlay } from '@fortawesome/free-solid-svg-icons';
import { createIntegrationNode, updateIntegrationNode } from '../../../../redux/slices/lists/integrationNodesSlice';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';
import api from '../../../../services/api';
import IntegrationNodeTestTab from './IntegrationNodeTestTab';
import TestIntegrationNodeModal from './TestIntegrationNodeModal';
import { v4 as uuidv4 } from 'uuid';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const COMMON_HEADERS = ['Accept', 'Authorization', 'Content-Type', 'User-Agent', 'X-API-Key', 'X-Request-ID', 'Cache-Control'];

const PARAMETER_TYPES = ['String', 'Number', 'File'];

interface ParameterItem {
  name: string;
  type: string;
  description: string;
  documentation: string;
  default_value: string;
}

interface HeaderItem {
  name: string;
  value: string;
  isCustom: boolean;
}

interface ResponseMappingEntry {
  id: string;
  path: string;
  type: 'string' | 'number';
  parameter_name: string;
  description: string;
  is_array: boolean;
}

interface ResponseMappingConfig {
  response_type: 'none' | 'string' | 'file' | 'json' | 'xml';
  parameter_name?: string;
  description?: string;
  node_file_name?: string;
  file_parameter_name?: string;
  file_description?: string;
  mappings?: ResponseMappingEntry[];
}

const RESPONSE_TYPE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'string', label: 'String' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
];

const MAPPING_TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
];

const RESPONSE_INFO_TEXT: Record<string, string> = {
  string: 'The entire response body will be saved as a single string value into the specified parameter.',
  json: 'Use JSONPath expressions to map values from the JSON response body to output parameters.',
  xml: 'Use XPath queries to map values from the XML response body to output parameters.',
};

const EditIntegrationNode = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { integrationId, nodeId } = useParams<{
    integrationId: string;
    nodeId: string;
  }>();
  const isEditMode = !!nodeId;

  const { zones } = useAppSelector((state) => state.listOfZones);
  const { loading } = useAppSelector((state) => state.listOfIntegrationNodes);

  const [activeTab, setActiveTab] = useState('basic');
  const [parentIntegration, setParentIntegration] = useState<any>(null);
  const [loadingData, setLoadingData] = useState(true);

  // Global request header
  const [httpMethod, setHttpMethod] = useState('GET');
  const [endpointPath, setEndpointPath] = useState('');
  const [overrideUrl, setOverrideUrl] = useState('');
  const [useOverrideUrl, setUseOverrideUrl] = useState(false);

  // Basic Properties
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [zoneId, setZoneId] = useState('');

  // Authentication
  const [inheritAuth, setInheritAuth] = useState(true);
  const [authBasic, setAuthBasic] = useState(false);
  const [authApiToken, setAuthApiToken] = useState(false);
  const [authOAuth2, setAuthOAuth2] = useState(false);

  // Parameters
  const [parameters, setParameters] = useState<ParameterItem[]>([]);
  const [touchedParams, setTouchedParams] = useState<Set<number>>(new Set());

  // Headers
  const [headers, setHeaders] = useState<HeaderItem[]>([]);
  const [touchedHeaders, setTouchedHeaders] = useState<Set<number>>(new Set());

  // Response Mapping
  const [responseMapping, setResponseMapping] = useState<ResponseMappingConfig>({ response_type: 'none' });

  // Test Modal
  const [showTestModal, setShowTestModal] = useState(false);
  const [unsaved, setUnsaved] = useState(false);

  // Fetch zones
  useEffect(() => {
    dispatch(
      getAuthorizedZones({
        resourceType: 'integrations',
        action: isEditMode ? 'update' : 'create',
      }) as any
    );
  }, [dispatch, isEditMode]);

  // Fetch parent integration and node data
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        // Always fetch parent integration for base URL
        const integrationRes = await api.get(`/integrations/${integrationId}`);
        setParentIntegration(integrationRes.data);

        // Default zone to parent's zone
        if (!isEditMode) {
          setZoneId(integrationRes.data.zone_id ? String(integrationRes.data.zone_id) : '');
          setUnsaved(false);
        }

        // In edit mode, fetch node data
        if (isEditMode && nodeId) {
          const nodeRes = await api.get(`/integrations/${integrationId}/nodes/${nodeId}`);
          const data = nodeRes.data;
          setHttpMethod(data.http_method || 'GET');
          setEndpointPath(data.endpoint_path || '');
          setOverrideUrl(data.override_url || '');
          setUseOverrideUrl(data.use_override_url || false);
          setName(data.name || '');
          setDescription(data.description || '');
          setZoneId(data.zone_id ? String(data.zone_id) : '');
          setInheritAuth(data.inherit_auth ?? true);
          setAuthBasic(data.auth_basic || false);
          setAuthApiToken(data.auth_api_token || false);
          setAuthOAuth2(data.auth_oauth2 || false);
          setParameters(data.parameters || []);
          setHeaders(
            (data.headers || []).map((h: any) => ({
              name: h.name || '',
              value: h.value || '',
              isCustom: !COMMON_HEADERS.includes(h.name),
            }))
          );
          setResponseMapping(data.response_mapping || { response_type: 'none' });
          setUnsaved(false);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    if (integrationId) fetchData();
  }, [integrationId, nodeId, isEditMode]);

  // ---- Validation ----
  const parameterErrors = useMemo(() => {
    const errors: Record<number, string> = {};
    const nameMap: Record<string, number[]> = {};

    parameters.forEach((p, i) => {
      const trimmed = p.name.trim().toLowerCase();
      if (!trimmed) {
        errors[i] = 'Name cannot be blank.';
      } else {
        if (!nameMap[trimmed]) nameMap[trimmed] = [];
        nameMap[trimmed].push(i);
      }
    });

    Object.values(nameMap).forEach((indices) => {
      if (indices.length > 1) {
        indices.forEach((i) => {
          errors[i] = 'Parameter names must be unique.';
        });
      }
    });

    return errors;
  }, [parameters]);

  const headerErrors = useMemo(() => {
    const errors: Record<number, string> = {};
    const nameMap: Record<string, number[]> = {};

    headers.forEach((h, i) => {
      const trimmed = h.name.trim().toLowerCase();
      if (!trimmed) {
        errors[i] = 'Header name cannot be blank.';
      } else {
        if (!nameMap[trimmed]) nameMap[trimmed] = [];
        nameMap[trimmed].push(i);
      }
    });

    Object.values(nameMap).forEach((indices) => {
      if (indices.length > 1) {
        indices.forEach((i) => {
          errors[i] = 'Duplicate header detected.';
        });
      }
    });

    return errors;
  }, [headers]);

  const visibleParamErrors = Object.keys(parameterErrors).filter((i) => touchedParams.has(Number(i)));
  const visibleHeaderErrors = Object.keys(headerErrors).filter((i) => touchedHeaders.has(Number(i)));

  const hasValidationErrors = Object.keys(parameterErrors).length > 0 || Object.keys(headerErrors).length > 0;

  // ---- Parameter handlers ----
  const addParameter = () => {
    setParameters((prev) => [
      ...prev,
      {
        name: '',
        type: 'String',
        description: '',
        documentation: '',
        default_value: '',
      },
    ]);
    setUnsaved(true);
  };

  const updateParameter = (index: number, field: string, value: string) => {
    setParameters((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setUnsaved(true);
  };

  const removeParameter = (index: number) => {
    setParameters((prev) => prev.filter((_, i) => i !== index));
    setUnsaved(true);
  };

  // ---- Header handlers ----
  const addHeader = () => {
    setHeaders((prev) => [...prev, { name: '', value: '', isCustom: false }]);
    setUnsaved(true);
  };

  const updateHeader = (index: number, field: string, value: any) => {
    if (field === 'name' && value !== '__custom__' && value !== '') {
      setTouchedHeaders((prev) => new Set(prev).add(index));
    }
    setHeaders((prev) => {
      const next = [...prev];
      if (field === 'name') {
        if (prev[index].isCustom) {
          // In custom mode, just update the name text — stay in custom mode
          next[index] = { ...next[index], name: value };
        } else if (value === '__custom__') {
          // Switching from dropdown to custom mode
          next[index] = { ...next[index], name: '', isCustom: true };
        } else {
          // Picking from the dropdown — stay in dropdown mode
          next[index] = { ...next[index], name: value, isCustom: false };
        }
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const removeHeader = (index: number) => {
    setHeaders((prev) => prev.filter((_, i) => i !== index));
    setUnsaved(true);
  };

  // ---- Response Mapping handlers ----
  const handleResponseTypeChange = (newType: string) => {
    const base: ResponseMappingConfig = {
      response_type: newType as ResponseMappingConfig['response_type'],
    };
    if (newType === 'json' || newType === 'xml') {
      base.mappings = [];
    }
    setResponseMapping(base);
    setUnsaved(true);
  };

  const addMappingRow = () => {
    setResponseMapping((prev) => ({
      ...prev,
      mappings: [
        ...(prev.mappings || []),
        {
          id: uuidv4(),
          path: '',
          type: 'string',
          parameter_name: '',
          description: '',
          is_array: false,
        },
      ],
    }));
    setUnsaved(true);
  };

  const updateMappingRow = (id: string, field: keyof ResponseMappingEntry, value: any) => {
    setResponseMapping((prev) => ({
      ...prev,
      mappings: (prev.mappings || []).map((m) => (m.id === id ? { ...m, [field]: value } : m)),
    }));
    setUnsaved(true);
  };

  const removeMappingRow = (id: string) => {
    setResponseMapping((prev) => ({
      ...prev,
      mappings: (prev.mappings || []).filter((m) => m.id !== id),
    }));
    setUnsaved(true);
  };

  // ---- Save ----
  const buildPayload = () => ({
    name,
    description,
    zone_id: zoneId ? Number(zoneId) : undefined,
    http_method: httpMethod,
    endpoint_path: endpointPath,
    override_url: overrideUrl,
    use_override_url: useOverrideUrl,
    inherit_auth: inheritAuth,
    auth_basic: authBasic,
    auth_api_token: authApiToken,
    auth_oauth2: authOAuth2,
    parameters,
    headers: headers.map((h) => ({ name: h.name, value: h.value })),
    response_mapping: responseMapping.response_type !== 'none' ? responseMapping : null,
  });

  const handleSave = async () => {
    if (hasValidationErrors) return;
    const payload = buildPayload();
    if (isEditMode) {
      const result = await dispatch(
        updateIntegrationNode({
          integrationId: Number(integrationId),
          nodeId: Number(nodeId),
          data: payload,
        }) as any
      );
      if (!result.error) setUnsaved(false);
    } else {
      const result = await dispatch(
        createIntegrationNode({
          integrationId: Number(integrationId),
          data: payload,
        }) as any
      );
      if (!result.error && result.payload?.node_id) {
        setUnsaved(false);
        navigate(`/configuration/integrations/${integrationId}/nodes/${result.payload.node_id}/edit`, { replace: true });
      }
    }
  };

  const handleSaveAndClose = async () => {
    if (hasValidationErrors) return;
    const payload = buildPayload();
    let result;
    if (isEditMode) {
      result = await dispatch(
        updateIntegrationNode({
          integrationId: Number(integrationId),
          nodeId: Number(nodeId),
          data: payload,
        }) as any
      );
    } else {
      result = await dispatch(
        createIntegrationNode({
          integrationId: Number(integrationId),
          data: payload,
        }) as any
      );
    }
    if (!result.error) {
      setUnsaved(false);
      navigate(`/configuration/integrations/${integrationId}/nodes`);
    }
  };

  const handleExit = () => {
    navigate(`/configuration/integrations/${integrationId}/nodes`);
  };

  const isSaving = loading.createIntegrationNode || loading.updateIntegrationNode;

  const baseUrl = parentIntegration?.base_url || '';
  const fullUrl = useOverrideUrl
    ? overrideUrl
    : baseUrl
      ? `${baseUrl}${endpointPath ? (endpointPath.startsWith('/') ? '' : '/') + endpointPath : ''}`
      : endpointPath;

  if (loadingData) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h3>Integration Node Configuration</h3>
          <p className="text-muted mb-0">Configure REST request details and properties for your integration node</p>
        </div>
        {isEditMode && (
          <Button variant="outline-success" onClick={() => setShowTestModal(true)}>
            <FontAwesomeIcon icon={faPlay} className="me-1" />
            Test
          </Button>
        )}
      </div>

      {/* Global Request Header */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fw-bold">HTTP Method</Form.Label>
                <Form.Select
                  value={httpMethod}
                  onChange={(e) => {
                    setHttpMethod(e.target.value);
                    setUnsaved(true);
                  }}
                >
                  {HTTP_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={useOverrideUrl ? 8 : 7}>
              <Form.Group>
                <Form.Label className="fw-bold">Endpoint</Form.Label>
                {useOverrideUrl ? (
                  <Form.Control
                    type="text"
                    placeholder="https://full-url.example.com/endpoint"
                    value={overrideUrl}
                    onChange={(e) => {
                      setOverrideUrl(e.target.value);
                      setUnsaved(true);
                    }}
                  />
                ) : (
                  <div className="d-flex">
                    {baseUrl && (
                      <div className="d-flex align-items-center px-3 bg-light border rounded-start text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {baseUrl}
                      </div>
                    )}
                    <Form.Control
                      type="text"
                      placeholder="/endpoint"
                      value={endpointPath}
                      onChange={(e) => {
                        setEndpointPath(e.target.value);
                        setUnsaved(true);
                      }}
                      className={baseUrl ? 'rounded-start-0' : ''}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={useOverrideUrl ? 2 : 3}>
              <Button
                variant={useOverrideUrl ? 'outline-secondary' : 'outline-primary'}
                onClick={() => {
                  setUseOverrideUrl(!useOverrideUrl);
                  setUnsaved(true);
                }}
                className="w-100"
              >
                {useOverrideUrl ? 'Use Base URL' : 'Override URL'}
              </Button>
            </Col>
          </Row>

          {fullUrl && (
            <div className="mt-2">
              <small className="text-muted">Full URL: {fullUrl}</small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Tabbed Content */}
      <Card className="mb-4">
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'basic')} className="mb-3">
            {/* ============ Basic Properties Tab ============ */}
            <Tab eventKey="basic" title="Basic Properties">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter node name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setUnsaved(true);
                    }}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter description"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setUnsaved(true);
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Zone</Form.Label>
                  <Form.Select
                    value={zoneId}
                    onChange={(e) => {
                      setZoneId(e.target.value);
                      setUnsaved(true);
                    }}
                  >
                    <option value="">Select a zone</option>
                    {(zones || []).map((zone: any) => (
                      <option key={zone.zone_id} value={zone.zone_id}>
                        {zone.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Defaults to parent Integration&apos;s Zone but can be edited</Form.Text>
                </Form.Group>
              </Form>
            </Tab>

            {/* ============ Authentication Tab ============ */}
            <Tab eventKey="authentication" title="Authentication">
              <div className="mb-3 p-3 bg-light rounded">
                <Form.Check
                  type="checkbox"
                  label="Inherit Authentication from parent Integration (Default)"
                  checked={inheritAuth}
                  onChange={(e) => {
                    setInheritAuth(e.target.checked);
                    setUnsaved(true);
                    if (e.target.checked) {
                      setAuthBasic(false);
                      setAuthApiToken(false);
                      setAuthOAuth2(false);
                    }
                  }}
                />
              </div>

              <p className="text-muted mb-3">Or select custom authentication methods:</p>

              <Form.Check
                type="radio"
                label="Basic Authentication"
                name="node_auth"
                checked={!inheritAuth && authBasic}
                disabled={inheritAuth}
                onChange={() => {
                  setAuthBasic(true);
                  setAuthApiToken(false);
                  setAuthOAuth2(false);
                  setUnsaved(true);
                }}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                label="API Token"
                name="node_auth"
                checked={!inheritAuth && authApiToken}
                disabled={inheritAuth}
                onChange={() => {
                  setAuthBasic(false);
                  setAuthApiToken(true);
                  setAuthOAuth2(false);
                  setUnsaved(true);
                }}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                label="OAuth 2.0"
                name="node_auth"
                checked={!inheritAuth && authOAuth2}
                disabled={inheritAuth}
                onChange={() => {
                  setAuthBasic(false);
                  setAuthApiToken(false);
                  setAuthOAuth2(true);
                  setUnsaved(true);
                }}
                className="mb-2"
              />
            </Tab>

            {/* ============ Parameters Tab ============ */}
            <Tab
              eventKey="parameters"
              title={
                <>
                  Parameters{' '}
                  {visibleParamErrors.length > 0 && (
                    <Badge bg="danger" className="ms-1">
                      {visibleParamErrors.length}
                    </Badge>
                  )}
                </>
              }
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0">Parameters</h5>
                  <small className="text-muted">Add multiple parameters with unique names</small>
                </div>
                <Button variant="outline-primary" size="sm" onClick={addParameter}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Parameter
                </Button>
              </div>

              {parameters.length === 0 && <p className="text-muted text-center py-4">No parameters added yet.</p>}

              {parameters.map((param, index) => (
                <Card key={index} className="mb-3 border">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h6 className="mb-0">Parameter</h6>
                      <Button variant="link" className="text-danger p-0" onClick={() => removeParameter(index)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Name <span className="text-danger">*</span> <small className="text-muted">(case-insensitive unique)</small>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Parameter name"
                            value={param.name}
                            onChange={(e) => updateParameter(index, 'name', e.target.value)}
                            onBlur={() => setTouchedParams((prev) => new Set(prev).add(index))}
                            isInvalid={touchedParams.has(index) && !!parameterErrors[index]}
                          />
                          {touchedParams.has(index) && parameterErrors[index] && (
                            <Form.Control.Feedback type="invalid">{parameterErrors[index]}</Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Type</Form.Label>
                          <Form.Select value={param.type} onChange={(e) => updateParameter(index, 'type', e.target.value)}>
                            {PARAMETER_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter description"
                        value={param.description}
                        onChange={(e) => updateParameter(index, 'description', e.target.value)}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Documentation</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter documentation"
                        value={param.documentation}
                        onChange={(e) => updateParameter(index, 'documentation', e.target.value)}
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Default Value</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter default value"
                        value={param.default_value}
                        onChange={(e) => updateParameter(index, 'default_value', e.target.value)}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              ))}
            </Tab>

            {/* ============ Headers Tab ============ */}
            <Tab
              eventKey="headers"
              title={
                <>
                  Headers{' '}
                  {visibleHeaderErrors.length > 0 && (
                    <Badge bg="danger" className="ms-1">
                      {visibleHeaderErrors.length}
                    </Badge>
                  )}
                </>
              }
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0">Headers</h5>
                  <small className="text-muted">Add HTTP headers to your request</small>
                </div>
                <Button variant="outline-primary" size="sm" onClick={addHeader}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Header
                </Button>
              </div>

              {headers.length === 0 && <p className="text-muted text-center py-4">No headers added yet.</p>}

              {headers.map((header, index) => (
                <Card key={index} className="mb-3 border">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h6 className="mb-0">Header</h6>
                      <Button variant="link" className="text-danger p-0" onClick={() => removeHeader(index)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>

                    <Row>
                      <Col md={5}>
                        <Form.Group>
                          <Form.Label>
                            Name <span className="text-danger">*</span>
                          </Form.Label>
                          {header.isCustom ? (
                            <Form.Control
                              type="text"
                              placeholder="Custom header name"
                              value={header.name}
                              onChange={(e) => updateHeader(index, 'name', e.target.value)}
                              onBlur={() => setTouchedHeaders((prev) => new Set(prev).add(index))}
                              isInvalid={touchedHeaders.has(index) && !!headerErrors[index]}
                            />
                          ) : (
                            <Form.Select
                              value={header.name}
                              onChange={(e) => updateHeader(index, 'name', e.target.value)}
                              isInvalid={touchedHeaders.has(index) && !!headerErrors[index]}
                            >
                              <option value="">Select a header</option>
                              {COMMON_HEADERS.map((h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                              <option value="__custom__">Custom...</option>
                            </Form.Select>
                          )}
                          {touchedHeaders.has(index) && headerErrors[index] && (
                            <Form.Control.Feedback type="invalid">{headerErrors[index]}</Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={7}>
                        <Form.Group>
                          <Form.Label>Value</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter header value"
                            value={header.value}
                            onChange={(e) => updateHeader(index, 'value', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </Tab>
            {isEditMode && (
              <Tab eventKey="test" title="Test">
                <IntegrationNodeTestTab
                  nodeId={Number(nodeId)}
                  integrationId={Number(integrationId)}
                  parameters={parameters}
                  inheritAuth={inheritAuth}
                  authBasic={authBasic}
                  authApiToken={authApiToken}
                  authOAuth2={authOAuth2}
                  unsaved={unsaved}
                />
              </Tab>
            )}

            {/* ============ Response Tab ============ */}
            <Tab eventKey="response" title="Response">
              <Form.Group className="mb-3 w-25" controlId="form-ResponseType">
                <Form.Label>Response Type</Form.Label>
                <Form.Select value={responseMapping.response_type} onChange={(e) => handleResponseTypeChange(e.target.value)}>
                  {RESPONSE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {responseMapping.response_type !== 'none' && RESPONSE_INFO_TEXT[responseMapping.response_type] && (
                <Alert variant="info" className="mb-3">
                  {RESPONSE_INFO_TEXT[responseMapping.response_type]}
                </Alert>
              )}

              {responseMapping.response_type === 'string' && (
                <>
                  <Form.Group className="mb-2" controlId="form-StringParamName">
                    <Form.Label>
                      Parameter Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={responseMapping.parameter_name || ''}
                      onChange={(e) =>
                        setResponseMapping((prev) => ({
                          ...prev,
                          parameter_name: e.target.value,
                        }))
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-2" controlId="form-StringDescription">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={responseMapping.description || ''}
                      onChange={(e) =>
                        setResponseMapping((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                    />
                  </Form.Group>
                </>
              )}

              {(responseMapping.response_type === 'json' || responseMapping.response_type === 'xml') && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h5 className="mb-0">{responseMapping.response_type === 'json' ? 'JSON' : 'XML'} Mappings</h5>
                    <Button size="sm" variant="outline-primary" onClick={addMappingRow}>
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Add Mapping
                    </Button>
                  </div>

                  {(responseMapping.mappings || []).length === 0 && (
                    <p className="text-muted mb-3">
                      No mappings defined yet. Click <em>Add Mapping</em> to create one.
                    </p>
                  )}

                  {(responseMapping.mappings || []).map((mapping) => (
                    <div key={mapping.id} className="border rounded p-3 mb-3">
                      <Row className="g-3 align-items-end">
                        <Col xs={12} md={3}>
                          <Form.Group>
                            <Form.Label>
                              {responseMapping.response_type === 'json' ? 'JSONPath' : 'XPath'} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={mapping.path}
                              onChange={(e) => updateMappingRow(mapping.id, 'path', e.target.value)}
                              placeholder={responseMapping.response_type === 'json' ? '$.data.value' : '/root/element'}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={2}>
                          <Form.Group>
                            <Form.Label>
                              Type <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select value={mapping.type} onChange={(e) => updateMappingRow(mapping.id, 'type', e.target.value)}>
                              {MAPPING_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={2}>
                          <Form.Group>
                            <Form.Label>
                              Parameter Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={mapping.parameter_name}
                              onChange={(e) => updateMappingRow(mapping.id, 'parameter_name', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={3}>
                          <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              type="text"
                              value={mapping.description}
                              onChange={(e) => updateMappingRow(mapping.id, 'description', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6} md={1}>
                          <Form.Check
                            type="checkbox"
                            label="Array"
                            checked={mapping.is_array}
                            onChange={(e) => updateMappingRow(mapping.id, 'is_array', e.target.checked)}
                          />
                        </Col>
                        <Col xs={6} md={1} className="d-flex">
                          <Button variant="outline-danger" className="ms-md-auto" onClick={() => removeMappingRow(mapping.id)} title="Remove mapping">
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Test Modal */}
      {isEditMode && integrationId && nodeId && (
        <TestIntegrationNodeModal
          show={showTestModal}
          onHide={() => setShowTestModal(false)}
          integrationId={integrationId}
          nodeId={nodeId}
          parameters={parameters}
          responseMapping={responseMapping}
        />
      )}

      {/* Bottom Buttons */}
      <div className="d-flex justify-content-end gap-2 mb-4">
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !name || hasValidationErrors}>
          Save
        </Button>
        <Button variant="primary" onClick={handleSaveAndClose} disabled={isSaving || !name || hasValidationErrors}>
          Save and Close
        </Button>
        <Button variant="danger" onClick={handleExit}>
          Exit
        </Button>
      </div>
    </div>
  );
};

export default EditIntegrationNode;
